/**
 * Broadcast-lists feed + mutations — all through the SDK (`sdk.broadcasts`).
 *
 *   const { broadcasts, create, rename, remove, addRecipients, removeRecipient,
 *           isLoading, error, refresh } = useBroadcasts()
 *
 * A broadcast list is owner-private: only its creator can see or send to it.
 * Sending fans out server-side into each recipient's DM with the owner, so
 * recipients receive an ordinary private message (and reply privately) — see
 * `sendBroadcast` in the SDK instance glue, used by `useSendMessage`.
 *
 * Local state, not the messenger store: the store's `broadcasts` slice was the
 * mock UI half, and these rows have no unread/typing/channel state to share.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Broadcast } from '../types'
import type { Broadcast as SdkBroadcast } from '@/sdk/instavcMessenger'
import {
  listBroadcasts,
  createBroadcast,
  updateBroadcast,
  deleteBroadcast,
  addBroadcastRecipients,
  removeBroadcastRecipient,
} from '@/sdk/instavcMessenger-instance'
import { useAuthStore } from '@/features/auth/store/auth.store'

interface UseBroadcasts {
  broadcasts: Broadcast[]
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
  create: (input: { name: string; recipientIds: string[]; avatarUrl?: string | null }) => Promise<Broadcast | null>
  rename: (id: string, name: string) => Promise<void>
  /** Change the name and/or picture; `avatarUrl: null` removes the picture. */
  update: (id: string, patch: { name?: string; avatarUrl?: string | null }) => Promise<Broadcast | null>
  remove: (id: string) => Promise<void>
  addRecipients: (id: string, userIds: string[]) => Promise<void>
  removeRecipient: (id: string, userId: string) => Promise<void>
}

function formatTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const t = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  if (d.toDateString() === now.toDateString()) return t
  return d.toLocaleDateString()
}

/** SDK shape → the UI shape the broadcast components already render. */
export function toUiBroadcast(b: SdkBroadcast): Broadcast {
  return {
    id: b.id,
    name: b.name ?? 'Broadcast',
    imageUrl: b.avatarUrl ?? undefined,
    lastMessage: b.lastMessage ?? 'No messages yet',
    time: formatTime(b.lastMessageAt),
    recipientCount: b.recipientCount,
    // A list is owner-only — there is nobody to receive FROM, so no unread.
    unreadCount: 0,
    recipients: b.recipients.map((r) => ({
      id: r.userId,
      name: r.name ?? `User ${r.userId.slice(0, 8)}`,
      imageUrl: r.avatarUrl ?? undefined,
    })),
  }
}

export function useBroadcasts(): UseBroadcasts {
  const myUserId = useAuthStore((s) => s.user?.id)
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const seededRef = useRef(false)

  const refresh = useCallback(async () => {
    if (!myUserId) return
    setIsLoading(true)
    setError(null)
    try {
      const items = await listBroadcasts(myUserId)
      setBroadcasts(items.map(toUiBroadcast))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load broadcasts')
      seededRef.current = false
    } finally {
      setIsLoading(false)
    }
  }, [myUserId])

  useEffect(() => {
    if (!myUserId || seededRef.current) return
    seededRef.current = true
    void refresh()
  }, [myUserId, refresh])

  const create = useCallback(
    async (input: { name: string; recipientIds: string[]; avatarUrl?: string | null }): Promise<Broadcast | null> => {
      if (!myUserId) return null
      setError(null)
      try {
        const created = toUiBroadcast(await createBroadcast(myUserId, input))
        setBroadcasts((prev) => [created, ...prev])
        return created
      } catch (err) {
        setError(err instanceof Error ? err.message : 'failed to create broadcast')
        return null
      }
    },
    [myUserId],
  )

  const update = useCallback(
    async (id: string, patch: { name?: string; avatarUrl?: string | null }): Promise<Broadcast | null> => {
      if (!myUserId) return null
      const updated = toUiBroadcast(await updateBroadcast(myUserId, id, patch))
      setBroadcasts((prev) => prev.map((b) => (b.id === id ? updated : b)))
      return updated
    },
    [myUserId],
  )

  const rename = useCallback(
    async (id: string, name: string) => {
      await update(id, { name })
    },
    [update],
  )

  const remove = useCallback(
    async (id: string) => {
      if (!myUserId) return
      await deleteBroadcast(myUserId, id)
      setBroadcasts((prev) => prev.filter((b) => b.id !== id))
    },
    [myUserId],
  )

  const addRecipients = useCallback(
    async (id: string, userIds: string[]) => {
      if (!myUserId) return
      await addBroadcastRecipients(myUserId, id, userIds)
      await refresh()
    },
    [myUserId, refresh],
  )

  const removeRecipient = useCallback(
    async (id: string, userId: string) => {
      if (!myUserId) return
      await removeBroadcastRecipient(myUserId, id, userId)
      await refresh()
    },
    [myUserId, refresh],
  )

  return { broadcasts, isLoading, error, refresh, create, rename, update, remove, addRecipients, removeRecipient }
}
